import CryptoJS from 'crypto-js';

// Use a secret key for encryption (in production, this should come from environment variables)
const SECRET_KEY = process.env.REACT_APP_URL_ENCRYPT_KEY || 'your-secret-key-here';

/**
 * Encrypt a value for use in URLs
 * @param {string|number} value - The value to encrypt
 * @returns {string} - Base64 encoded encrypted string
 */
export const encryptForUrl = (value) => {
  try {
    const encrypted = CryptoJS.AES.encrypt(value.toString(), SECRET_KEY).toString();
    // Make URL safe by replacing problematic characters
    return encodeURIComponent(encrypted);
  } catch (error) {
    console.error('Encryption error:', error);
    return value.toString();
  }
};

/**
 * Decrypt a value from URL
 * @param {string} encryptedValue - The encrypted value from URL
 * @returns {string} - Decrypted original value
 */
export const decryptFromUrl = (encryptedValue) => {
  try {
    const decoded = decodeURIComponent(encryptedValue);
    const decrypted = CryptoJS.AES.decrypt(decoded, SECRET_KEY);
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedValue;
  }
};

/**
 * Generate a secure token for course access
 * @param {string|number} courseId - The course ID
 * @param {number} timestamp - Optional timestamp for expiration
 * @returns {string} - Secure token
 */
export const generateCourseToken = (courseId, timestamp = Date.now()) => {
  const payload = {
    courseId: courseId.toString(),
    timestamp,
    // Add expiration (24 hours from now)
    expires: timestamp + (24 * 60 * 60 * 1000)
  };
  
  return encryptForUrl(JSON.stringify(payload));
};

/**
 * Validate and extract course ID from secure token
 * @param {string} token - The secure token
 * @returns {object} - { valid: boolean, courseId: string|null, expired: boolean }
 */
export const validateCourseToken = (token) => {
  try {
    const decrypted = decryptFromUrl(token);
    const payload = JSON.parse(decrypted);
    
    const now = Date.now();
    const expired = now > payload.expires;
    
    return {
      valid: !expired && payload.courseId,
      courseId: payload.courseId,
      expired
    };
  } catch (error) {
    console.error('Token validation error:', error);
    return {
      valid: false,
      courseId: null,
      expired: true
    };
  }
};

/**
 * Generate a secure token for organization access
 * @param {string|number} orgId - The organization ID
 * @param {number} timestamp - Optional timestamp for expiration
 * @returns {string} - Secure token
 */
export const generateOrganizationToken = (orgId, timestamp = Date.now()) => {
  const payload = {
    orgId: orgId.toString(),
    timestamp,
    // Add expiration (24 hours from now)
    expires: timestamp + (24 * 60 * 60 * 1000)
  };
  
  return encryptForUrl(JSON.stringify(payload));
};

/**
 * Validate and extract organization ID from secure token
 * @param {string} token - The secure token
 * @returns {object} - { valid: boolean, orgId: string|null, expired: boolean }
 */
export const validateOrganizationToken = (token) => {
  try {
    const decrypted = decryptFromUrl(token);
    const payload = JSON.parse(decrypted);
    
    const now = Date.now();
    const expired = now > payload.expires;
    
    return {
      valid: !expired && payload.orgId,
      orgId: payload.orgId,
      expired
    };
  } catch (error) {
    console.error('Token validation error:', error);
    return {
      valid: false,
      orgId: null,
      expired: true
    };
  }
};

/**
 * Generate secure URL for course modules
 * @param {string|number} courseId - The course ID
 * @param {string} context - The context ('admin' or 'user'), defaults to current location
 * @returns {string} - Secure URL for course modules
 */
export const generateModulesUrl = (courseId, context) => {
  const encryptedId = generateCourseToken(courseId);
  
  // Auto-detect context if not provided
  if (!context) {
    const currentPath = window.location.pathname;
    context = currentPath.includes('/admin') ? 'admin' : 'user';
  }
  
  if (context === 'admin') {
    return `/admin/course-modules/${encryptedId}`;
  } else {
    return `/user/course-modules/${encryptedId}`;
  }
};

/**
 * Generate secure URL for course enrollments
 * @param {string|number} courseId - The course ID
 * @param {string} context - The context ('admin' or 'user'), defaults to current location
 * @returns {string} - Secure URL for course enrollments
 */
export const generateEnrollmentsUrl = (courseId, context) => {
  const encryptedId = generateCourseToken(courseId);
  
  // Auto-detect context if not provided
  if (!context) {
    const currentPath = window.location.pathname;
    context = currentPath.includes('/admin') ? 'admin' : 'user';
  }
  
  if (context === 'admin') {
    return `/admin/course-enrollments/${encryptedId}`;
  } else {
    return `/user/course-enrollments/${encryptedId}`;
  }
};

/**
 * Validate and extract course ID from URL parameter (alias for validateCourseToken)
 * @param {string} encryptedParam - The encrypted parameter from URL
 * @returns {object} - Validation result with courseId
 */
export const validateNavigationToken = (encryptedParam) => {
  if (!encryptedParam) {
    return {
      valid: false,
      error: 'No navigation token provided',
      courseId: null
    };
  }
  
  return validateCourseToken(encryptedParam);
};
